import apiClient from '../client';
import type { StaffAttendanceStatus } from './staff-attendance';

/** Summary counters shown as chips on the teacher dashboard. */
export interface TeacherCounters {
  homework_active:  number;
  attendance_today: number;
  total_students:   number;
}

/** Status snapshot for today's class attendance. */
export interface AttendanceTodaySnapshot {
  marked_count:   number;
  last_marked_at: string | null;
  status:         'marked' | 'pending';
}

export interface LatestHomework {
  id:         number;
  title:      string;
  subject:    string | null;
  due_date:   string | null;
  created_at: string;
  class_name: string | null;
}

export interface DashboardEvent {
  id:           number;
  title:        string;
  event_type:   string;
  start_date:   string;
  end_date:     string | null;
  is_published: 0 | 1;
}

export type CelebrationKind = 'student' | 'staff' | 'wedding' | 'work';

export interface Celebration {
  kind:        CelebrationKind;
  id:          number;
  name:        string;
  subtitle:    string | null;
  branch_slug: string;
  /** Google Drive / uploads URL of the person's photo — null when missing. */
  photo_url?:  string | null;
  /** Human-readable event label ("Student Birthday", "Work Anniversary", …). */
  event_label?: string;
  /** Full years since the celebrated date (null when input was invalid). */
  years:       number | null;
}

export interface CelebrationsToday {
  birthdays:    Celebration[];
  anniversaries: Celebration[];
}

/** Payload of GET /dashboard/celebrations — a flat, presentation-ready list. */
export interface DashboardCelebrations {
  /** ISO date (YYYY-MM-DD) the server evaluated CURDATE() against. */
  date:          string;
  total:         number;
  celebrations:  Celebration[];
}

export interface TeacherSummary {
  branch:            string | null;
  counters:          TeacherCounters;
  last_homework:     LatestHomework | null;
  attendance_today:  AttendanceTodaySnapshot;
  events_today:      DashboardEvent[];
  celebrations_today: CelebrationsToday;
  punch_status:      StaffAttendanceStatus;
}

const BASE = '/dashboard';

export const adminDashboardApi = {
  /** Single round-trip aggregate for the Teacher Dashboard. */
  getTeacherSummary: () =>
    apiClient.get<{ data: TeacherSummary }>(`${BASE}/teacher-summary`).then((r) => r.data.data),

  /**
   * Today's unified celebrations list for the Super Admin dashboard.
   * Returns a flat array with person name, event type, photo URL and years.
   */
  getCelebrations: () =>
    apiClient.get<{ data: DashboardCelebrations }>(`${BASE}/celebrations`).then((r) => r.data.data),
};

